import { hiringRequestQueries } from 'queryKeys/hiringRequest';
import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { deleteHiringRequestScorecard, editHiringRequestScorecard } from './queries';
import { HiringRequestScorecard } from '../types';
import { CreateScorecardMutationVariables } from './Panes/Create/Scorecard/mutations';
import { AxiosError } from 'axios';

export interface DeleteScorecardMutationVariables {
  scorecardId: number;
  hiringRequestId: number;
}

export interface EditScorecardMutationVariables extends CreateScorecardMutationVariables {
  scorecardId: number;
}

export function useEditHiringRequestScorecard(
  options?: Omit<
    UseMutationOptions<HiringRequestScorecard, AxiosError, EditScorecardMutationVariables, unknown>,
    'mutationFn'
  >
): UseMutationResult<HiringRequestScorecard, AxiosError, EditScorecardMutationVariables, unknown> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ hiringRequestId, scorecardId, payload }) =>
      editHiringRequestScorecard(hiringRequestId, scorecardId, payload),
    ...options,
    onSuccess: (data, variables, context) => {
      options.onSuccess?.(data, variables, context);

      const currentQueryData: HiringRequestScorecard[] = queryClient.getQueryData(
        hiringRequestQueries.hiringRequests.scorecards(variables.hiringRequestId).queryKey
      );

      const updatedData = currentQueryData.map((scorecard) =>
        scorecard.id === data.id ? data : scorecard
      );

      queryClient.setQueryData(
        hiringRequestQueries.hiringRequests.scorecards(variables.hiringRequestId).queryKey,
        updatedData
      );
    },
  });
}

export function useDeleteHiringRequestScorecard(
  options?: Omit<
    UseMutationOptions<HiringRequestScorecard, unknown, DeleteScorecardMutationVariables, unknown>,
    'mutationFn'
  >
): UseMutationResult<HiringRequestScorecard, unknown, DeleteScorecardMutationVariables, unknown> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ hiringRequestId, scorecardId }) => {
      const response = await deleteHiringRequestScorecard(hiringRequestId, scorecardId);
      return response;
    },
    ...options,
    onSuccess: (data, variables, context) => {
      options.onSuccess?.(data, variables, context);

      const currentQueryData: HiringRequestScorecard[] = queryClient.getQueryData(
        hiringRequestQueries.hiringRequests.scorecards(variables.hiringRequestId).queryKey
      );

      const updatedData = currentQueryData.filter((datum) => datum.id !== variables.scorecardId);

      queryClient.setQueryData(
        hiringRequestQueries.hiringRequests.scorecards(variables.hiringRequestId).queryKey,
        updatedData
      );
    },
  });
}
